import React, { useEffect, useState } from "react";
import GoogleMapReact from "google-map-react";
import { useSelector } from "react-redux";

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";

interface Props {
  collections: any[];
  deliveries: any[];
}

const DistanceMapView: React.FC<Props> = ({ collections, deliveries }) => {
  const [map, setMap] = useState<any>(null);
  const [maps, setMaps] = useState<any>(null);
  const [totalDistance, setTotalDistance] = useState<number>(0);

  const { userProfile } = useSelector((state: any) => state?.user);

  const transportStop = userProfile?.transportCode;

  const stops = [
    transportStop,
    ...collections.map((c) => c.postcode),
    ...deliveries.map((d) => d.postcode),
    transportStop,
  ].filter(Boolean);

  const stopLabels = [
    "T",
    ...collections.map((_: any, i: number) => `C${i + 1}`),
    ...deliveries.map((_: any, i: number) => `D${i + 1}`),
    "T",
  ];

  useEffect(() => {
    if (!map || !maps || stops.length < 2) return;

    const directionsService = new maps.DirectionsService();

    const directionsRenderer = new maps.DirectionsRenderer({
      draggable: false,
      suppressMarkers: true,
      polylineOptions: {
        strokeColor: "#1976d2",
        strokeWeight: 5,
      },
    });

    directionsRenderer.setMap(map);

    const geocoder = new maps.Geocoder();

    const getAddress = (postcode: string) => {
      return new Promise<string>((resolve) => {
        geocoder.geocode({ address: postcode }, (results: any, status: any) => {
          if (status === "OK" && results[0]) {
            resolve(results[0].formatted_address);
          } else {
            resolve(postcode);
          }
        });
      });
    };

    const waypoints = stops.slice(1, -1).map((stop) => ({
      location: stop,
      stopover: true,
    }));

    directionsService.route(
      {
        origin: stops[0],
        destination: stops[stops.length - 1],
        waypoints,
        optimizeWaypoints: false,
        travelMode: maps.TravelMode.DRIVING,
      },
      async (result: any, status: any) => {
        if (status === "OK") {
          directionsRenderer.setDirections(result);

          const route = result.routes[0];

          let distance = 0;
          route.legs.forEach((leg: any) => {
            distance += leg.distance.value;
          });

          const miles = (distance / 1000) * 0.621371;
          setTotalDistance(miles);

          const legs = route.legs;

          const createMarker = async (
            position: any,
            label: string,
            postcode: string
          ) => {
            const address = await getAddress(postcode);

            new maps.Marker({
              position,
              map,
              label,
              title: address, // tooltip on hover
            });
          };

          await createMarker(
            legs[0].start_location,
            stopLabels[0],
            stops[0]
          );

          for (let i = 0; i < legs.length; i++) {
            await createMarker(
              legs[i].end_location,
              stopLabels[i + 1],
              stops[i + 1]
            );
          }
        }
      }
    );
  }, [map, maps, collections, deliveries, transportStop]);

  return (
    <div>
      <div style={{ marginBottom: 10, fontSize: 16 }}>
        <b>Total Distance:</b> {totalDistance.toFixed(2)} miles
      </div>

      <div style={{ height: "450px", width: "100%" }}>
        <GoogleMapReact
          bootstrapURLKeys={{ key: GOOGLE_MAPS_API_KEY }}
          defaultCenter={{ lat: 51.5074, lng: -0.1278 }}
          defaultZoom={6}
          yesIWantToUseGoogleMapApiInternals
          onGoogleApiLoaded={({ map, maps }) => {
            setMap(map);
            setMaps(maps);
          }}
        />
      </div>
    </div>
  );
};

export default DistanceMapView;